import React, { useEffect, useState, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { MapContainer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import * as pmtiles from "pmtiles";
import * as turf from '@turf/turf';
import 'leaflet/dist/leaflet.css';
import { useGeoData } from '../contexts/GeoDataContext';

// Fix for default marker icons in Leaflet with React
delete (L.Icon.Default.prototype as any)._getIconUrl;

interface MapComponentRef {
  invalidateSize: () => void;
}

interface MapComponentProps {
  selectedZipCodes: string[];
  /** First 3 ZIPs of the subscription (FREE tier Kernzone). When provided, kern and rand zones get different fill colors. */
  kernZipCodes?: string[];
  onAddZip?: (zip: string) => void;
  onRemoveZip?: (zip: string) => void;
  /** Small non-interactive preview (desktop editor sidebar): no pan/zoom, no neighbours, no clicks. */
  preview?: boolean;
}

/**
 * Component to handle PMTiles integration with Leaflet
 */
const PMTilesLayer: React.FC = () => {
  const map = useMap();
  const layerRef = useRef<L.Layer | null>(null);

  useEffect(() => {
    const pmtilesUrl = process.env.REACT_APP_PMTILES_URL || "https://tiles.schneller-wohnen.net/germany-raster-0-14.pmtiles";
    const p = new pmtiles.PMTiles(pmtilesUrl);
    
    // Create the raster layer using the pmtiles plugin for Leaflet
    const layer = pmtiles.leafletRasterLayer(p, {
      maxNativeZoom: 14,
      maxZoom: 15,
      attribution: '© OpenStreetMap contributors',
      className: 'map-tiles'
    });

    layer.addTo(map);
    layerRef.current = layer;

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
      }
    };
  }, [map]);

  return null;
};

/**
 * Internal component to handle map movement when data changes
 */
const MapController: React.FC<{ data: any }> = ({ data }) => {
  const map = useMap();
  const lastZipsRef = useRef<string>('');
  
  useEffect(() => {
    if (!data || !data.features || data.features.length === 0) return;
    
    const zipsKey = data.features.map((f: any) => f.properties.plz).sort().join(',');
    if (zipsKey === lastZipsRef.current) return;
    lastZipsRef.current = zipsKey;

      try {
        const layer = L.geoJSON(data);
        const bounds = layer.getBounds();
      
        if (bounds.isValid()) {
            const padding = L.point(7, 7); 
            map.invalidateSize();
            map.fitBounds(bounds, { padding, animate: false });
        }
      } catch (e) {
        console.error('Error fitting bounds:', e);
    }
}, [data, map]);
   
  return null;
};

const MapComponent = forwardRef<MapComponentRef, MapComponentProps>(
  ({ selectedZipCodes, kernZipCodes, onAddZip, onRemoveZip, preview = false }, ref) => {
  // Use centralized data loading
  const { polygons, loadPolygons, isLoadingPolygons, errorPolygons } = useGeoData();
  const [geoData, setGeoData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mapRef = useRef<L.Map | null>(null);

  useImperativeHandle(ref, () => ({
    invalidateSize: () => {
      mapRef.current?.invalidateSize();
    },
  }));

  useEffect(() => {
    loadPolygons();
  }, [loadPolygons]);

  useEffect(() => {
    setGeoData(polygons);
    setIsLoading(isLoadingPolygons);
    setError(errorPolygons);
  }, [polygons, isLoadingPolygons, errorPolygons]);

  const { filteredData, neighborsData } = useMemo(() => {
    if (!geoData || !selectedZipCodes.length) return { filteredData: null, neighborsData: null };
    
    const selectedFeatures = geoData.features.filter((f: any) => 
      f.properties && f.properties.plz && selectedZipCodes.includes(f.properties.plz)
    );

    if (selectedFeatures.length === 0) return { filteredData: null, neighborsData: null };

    const filtered = {
      ...geoData,
      features: selectedFeatures
    };

    if (preview) return { filteredData: filtered, neighborsData: null };

    // Calculate neighbors
    const neighbors = geoData.features.filter((f: any) => {
        const plz = f.properties?.plz;
        if (!plz || selectedZipCodes.includes(plz)) return false;
        
        const fBox = turf.bbox(f);
        
        return selectedFeatures.some((sf: any) => {
            const sfBox = turf.bbox(sf);
            const expSfBox = [sfBox[0]-0.02, sfBox[1]-0.02, sfBox[2]+0.02, sfBox[3]+0.02];
            
            if (fBox[0] > expSfBox[2] || fBox[2] < expSfBox[0] ||
                fBox[1] > expSfBox[3] || fBox[3] < expSfBox[1]) {
                return false;
            }
            
            try {
                return turf.booleanIntersects(sf, f);
            } catch (e) {
                return false;
            }
        });
    });

    const neighborsFiltered = {
        ...geoData,
        features: neighbors
    };

    return { filteredData: filtered, neighborsData: neighborsFiltered };
  }, [geoData, selectedZipCodes, preview]);

  if (!selectedZipCodes || selectedZipCodes.length === 0) {
    return (
      <div className="flex h-[150px] items-center justify-center rounded-control border border-dashed border-line-strong bg-sunken text-sm text-muted">
        Wähle mindestens eine PLZ aus, um die Karte anzuzeigen.
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-danger-text">{error}</p>;
  }

  if (isLoading) {
    return (
        <div className="flex h-[250px] items-center justify-center rounded-control border border-dashed border-line-strong bg-sunken text-sm text-muted">
            Lade Kartendaten…
        </div>
    );
  }

  const neighborStyle = (feature: any) => ({
    color: '#94a3b8',
    weight: 1,
    opacity: 0.3,
    fillColor: '#cbd5e1',
    fillOpacity: 0.1,
  });

  return (
    <div
      className={
        preview
          ? 'pointer-events-none relative h-full w-full overflow-hidden rounded-control'
          : 'h-[450px] w-full overflow-hidden rounded-control border border-line shadow-card'
      }
    >
      <MapContainer
        center={[51.1657, 10.4515]} 
        zoom={6} 
        scrollWheelZoom={!preview}
        dragging={!preview}
        doubleClickZoom={!preview}
        touchZoom={!preview}
        boxZoom={!preview}
        keyboard={!preview}
        zoomControl={!preview}
        // The preview is too small for Leaflet's attribution bar; it renders a compact one below.
        attributionControl={!preview}
        minZoom={preview ? 5 : 8}
        maxZoom={15}
        className="h-full w-full"
        ref={(el: any) => { if (el?.leafletElement) mapRef.current = el.leafletElement; }}
      >
        <PMTilesLayer />
        {!preview && neighborsData && (
          <GeoJSON 
            key={`neighbors-${selectedZipCodes.join(',')}`}
            data={neighborsData}
            style={neighborStyle}
            onEachFeature={(feature, layer) => {
              if (feature.properties?.plz) {
                layer.bindTooltip(`PLZ ${feature.properties.plz} hinzufügen`, { sticky: true, opacity: 0.9, interactive: false });
              }
            }}
            eventHandlers={{
              click: (e) => {
                L.DomEvent.stopPropagation(e);
                const plz = e.target.feature?.properties?.plz;
                if (plz && onAddZip) onAddZip(plz);
              },
              mouseover: (e) => {
                const l = e.target;
                l.setStyle({ fillOpacity: 0.5, opacity: 0.9, weight: 3, color: '#334155' });
                if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) l.bringToFront();
              },
              mouseout: (e) => { e.target.setStyle(neighborStyle(e.target.feature)); },
            }}
          />
        )}
        {filteredData && (
          <>
            <GeoJSON 
              key={`selected-${selectedZipCodes.join(',')}-${(kernZipCodes || []).join(',')}`}
              data={filteredData} 
              style={(feature) => {
                const plz = feature?.properties?.plz;
                const isKern = !kernZipCodes || kernZipCodes.length === 0 || kernZipCodes.includes(plz);
                return {
                  color: isKern ? '#2563eb' : '#6b7280',
                  weight: 2,
                  opacity: 0.8,
                  fillColor: isKern ? '#3b82f6' : '#9ca3af',
                  fillOpacity: 0.4,
                };
              }}
              onEachFeature={(feature, layer) => {
                if (feature.properties?.plz) {
                  layer.bindTooltip(`PLZ ${feature.properties.plz} entfernen`, { sticky: true, opacity: 0.9, interactive: false });
                }
              }}
              eventHandlers={{
                click: (e) => {
                  L.DomEvent.stopPropagation(e);
                  const plz = e.target.feature?.properties?.plz;
                  if (plz && onRemoveZip) onRemoveZip(plz);
                },
                mouseover: (e) => {
                  const l = e.target;
                  l.setStyle({ fillOpacity: 0.6, weight: 3 });
                },
                mouseout: (e) => {
                  const l = e.target;
                  l.setStyle({ fillOpacity: 0.4, weight: 2 });
                },
              }}
            />
            <MapController data={filteredData} />
          </>
        )}
      </MapContainer>
      {preview && (
        <span className="absolute bottom-0 left-0 z-[500] rounded-tr bg-card/85 px-1 text-[9px] leading-4 text-muted">
          © OpenStreetMap
        </span>
      )}
    </div>
  );
});

MapComponent.displayName = 'MapComponent';

export { MapComponent };
